"use client";

/**
 * The three-stage read path, as a diagram that runs itself, with the L0 head start shown BESIDE it.
 *
 * L0 IS NOT A STAGE OF THE CHAIN (corrected 2026-10-06). Pages do not start in L0 and L1 does not
 * wait on it. Some pages are filled in ahead of our own readers; once our reading reaches them it
 * takes over. So L0 is drawn apart from the L1 -> L2 -> L3 hand-off, never as its first hop.
 *
 * NAMED L0/L1/L2/L3 AND NOTHING MORE. The stages are deliberately not described by the technique each
 * one uses: naming the method is exactly the detail that lets someone defeat it, and this page is
 * public. What a reader needs is that there are three, that each is more capable and more expensive
 * than the last, and that almost nothing reaches the third — which is the actual competitive point.
 *
 * FIGURES ARE APPROXIMATE AND MEASURED, NOT ASPIRATIONAL. Re-measured 2026-09-27 over 14 days of
 * index_job_durations: L1 97.4%, L2 1.6%, L3 1.0% (2,991,549 / 50,090 / 30,129 jobs). On
 * 2026-08-12 the dashboard had read L1 64%, L2 36%, L3 0.1% of attributed pages. An earlier version of this component
 * claimed 99%/1%/<0.1%, which overstated L1 by a third and understated L2 roughly thirtyfold — a
 * number nobody had re-checked since it was written. Re-verify before quoting these anywhere.
 *
 * The animation is a loop with a pause between passes, not a scrub-on-scroll: the sequence IS the
 * message (a page falls through stages until one succeeds), and a reader who arrives mid-scroll
 * should still see a whole pass without touching anything.
 */

import { useEffect, useState } from "react";

/* L0 sits beside the chain, not in front of it. Named and nothing more, like the rest — and it
   carries no share, because no figure has been measured for it yet. Do not invent one here. */
const HEAD_START = {
  id: "L0",
  name: "Head start",
  line: "Some pages are ready before we even get to them. Then we read them ourselves.",
};

const STAGES = [
  {
    id: "L1",
    name: "First pass",
    share: "≈ 97 in 100",
    line: "Almost every page is read here, in milliseconds, at almost no cost.",
    tone: "sky",
  },
  {
    id: "L2",
    name: "Second pass",
    share: "≈ 2 in 100",
    line: "The few that need more work to read properly escalate automatically.",
    tone: "amber",
  },
  {
    id: "L3",
    name: "Assisted",
    share: "≈ 1 in 100",
    line: "The stubborn few — handled with the heaviest tools, only where it is warranted.",
    tone: "violet",
  },
];

const TONE: Record<string, { dot: string; ring: string; text: string }> = {
  sky:    { dot: "bg-sky-400",    ring: "ring-sky-400/40",    text: "text-sky-500 dark:text-sky-400" },
  amber:  { dot: "bg-amber-400",  ring: "ring-amber-400/40",  text: "text-amber-600 dark:text-amber-400" },
  violet: { dot: "bg-violet-400", ring: "ring-violet-400/40", text: "text-violet-500 dark:text-violet-400" },
};

export function IndexStagesDiagram() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    // Respect a reader who has asked for less motion: show the finished state, animate nothing.
    const reduce = typeof window !== "undefined"
      && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setActive(STAGES.length); return; }

    let i = 0;
    const t = setInterval(() => {
      i = (i + 1) % (STAGES.length + 2);   // +2 = a beat of stillness between passes
      setActive(i);
    }, 1400);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="relative rounded-2xl border hairline-strong bg-card overflow-hidden">
      <div className="border-b hairline px-6 py-4 flex items-center justify-between">
        <span className="text-xs font-mono uppercase tracking-[0.15em] text-foreground/50">
          How a page gets read
        </span>
        <span className="text-xs font-mono text-foreground/40">3 stages · automatic · no queue-jumping</span>
      </div>

      <div className="p-6 md:p-10">
        <div className="flex flex-col gap-4 md:flex-row md:items-stretch md:gap-0">
          {STAGES.map((s, i) => {
            const tone = TONE[s.tone];
            const on = active > i;
            return (
              <div key={s.id} className="contents">
                <div
                  className={[
                    "flex-1 rounded-xl border p-5 transition-all duration-700",
                    on ? `hairline-strong ring-1 ${tone.ring} bg-foreground/[0.03]` : "hairline opacity-55",
                  ].join(" ")}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={[
                        "inline-flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold text-white transition-colors duration-700",
                        on ? tone.dot : "bg-foreground/25",
                      ].join(" ")}
                    >
                      {s.id}
                    </span>
                    <span className="text-xs font-mono uppercase tracking-[0.12em] text-foreground/55">
                      {s.name}
                    </span>
                  </div>
                  <p className={`mt-3 font-mono text-lg font-semibold ${on ? tone.text : "text-foreground/40"}`}>
                    {s.share}
                  </p>
                  <p className="mt-1 text-xs leading-relaxed text-foreground/60">{s.line}</p>
                </div>

                {i < STAGES.length - 1 && (
                  <div className="flex items-center justify-center px-2 md:px-3">
                    <span
                      className={[
                        "text-[10px] font-mono whitespace-nowrap transition-opacity duration-700",
                        active > i + 1 ? "opacity-100 text-foreground/45" : "opacity-25 text-foreground/40",
                      ].join(" ")}
                    >
                      only if needed →
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* L0 apart from the chain: dashed, its own row, and an arrow into the index rather than into
            L1, so it cannot be read as where a page starts. */}
        <div className="mt-6 flex flex-col gap-3 rounded-xl border border-dashed border-emerald-400/50 bg-emerald-400/[0.04] p-5 sm:flex-row sm:items-center sm:gap-5">
          <div className="flex items-center gap-2 shrink-0">
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-400 text-[10px] font-bold text-white">
              {HEAD_START.id}
            </span>
            <span className="text-xs font-mono uppercase tracking-[0.12em] text-foreground/55">
              {HEAD_START.name}
            </span>
          </div>
          <p className="text-xs leading-relaxed text-foreground/60 sm:flex-1">{HEAD_START.line}</p>
          <span className="text-[10px] font-mono whitespace-nowrap text-emerald-600 dark:text-emerald-400 shrink-0">
            straight into the index →
          </span>
        </div>

        <p className="mt-8 text-sm leading-relaxed text-foreground/70">
          A page moves to the next stage only when the one before it could not read it properly.
          That is the whole cost model: the heaviest machinery runs on well under a percent of what
          we read, so reading all of it stays affordable.
        </p>
      </div>
    </div>
  );
}
